import {observer} from 'mobx-react-lite'
import React, {useRef} from 'react'
import tw from 'tailwind-styled-components'
import {ChatMessage, DataObjectId} from 'yougile-model'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {Menu} from '@components/menu'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {SearchResults} from '@components/right-panel/search/search-results'
import {useBoolean, useClickAway, useGlobalKeyDown} from '@containers/hooks'
import {useMessageForwardingStore} from '../message-forwarding-store'
import {Header} from './header'
import {List} from './list'
import {Search} from './search'

const TwMenuOverlay = tw.div`
  h-screen
  bg-menu-active-element-old
  absolute
  left-0
  opacity-50
  bottom-0
  right-0
  z-1000
`

const TwMenuContainer = tw.div`
  absolute
  select-none
  text-14
  left-0
  bottom-0
  right-0
  h-screen
  z-2000
`

const TwPopupContainer = tw.div`
  -translate-x-1/2
  -translate-y-1/2
  absolute
  bg-main-old
  left-1/2
  top-1/2
  transform
  overflow-hidden
  h-3/4
  rounded-4
`

const TwSearch = tw.div<{$shadow: boolean}>`
  ${p => (p.$shadow ? 'shadow-bottom-old z-10' : '')}
`

interface Props {
  onSelect: (message: ChatMessage | undefined) => void
}

export const MessageForwardingPopupBase = observer(({onSelect}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const chatWidth = rightPanelStore.state.chatPanel.width
  const [state, store] = useMessageForwardingStore()
  const scrollTop = useBoolean(true)
  const close = (): void => onSelect(undefined)
  useClickAway(ref, close)
  useGlobalKeyDown({
    escape: () => {
      close()
      return true
    }
  })
  const onItemClick = (chatId: DataObjectId): void =>
    store.itemClick(chatId, onSelect)

  return (
    <Menu withPropagate recalcWhenResize bottomZero onEscape={close}>
      <TwMenuOverlay style={{width: `${chatWidth}px`}} onClick={close} />
      <TwMenuContainer style={{width: `${chatWidth}px`}}>
        <TwPopupContainer ref={ref} style={{width: chatWidth - 32}}>
          <div className="flex flex-col h-full">
            <Header onSelect={onSelect} />
            <TwSearch $shadow={!scrollTop.value}>
              <Search />
            </TwSearch>
            <WithPerfectScrollbar
              onScrollY={x => scrollTop.setValue(x.scrollTop === 0)}
            >
              {state.query.length ? (
                <SearchResults search={state.query} onItemClick={onItemClick} />
              ) : (
                <List onSelect={onSelect} />
              )}
            </WithPerfectScrollbar>
          </div>
        </TwPopupContainer>
      </TwMenuContainer>
    </Menu>
  )
})
